import React, { createContext, useContext, useState, useEffect } from 'react';

const InventoryContext = createContext();

export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
};

export const InventoryProvider = ({ children }) => {
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({
    model: '',
    year: '',
    priceRange: '',
    type: '',
    transmission: '',
    fuelType: '',
    bodyStyle: '',
    drivetrain: '',
    condition: '',
  });

  // Enhanced Audi vehicle data
  useEffect(() => {
    const audiVehicles = [
      {
        id: 1,
        make: 'Audi',
        model: 'Q3',
        year: 2024,
        price: 42800,
        type: 'SUV',
        transmission: 'S tronic',
        fuelType: 'Gasoline',
        mileage: 15,
        exterior: 'Navarra Blue Metallic',
        interior: 'Black Milano Leather',
        features: ['quattro AWD', 'Audi Virtual Cockpit', 'MMI Navigation Plus', 'Panoramic Sunroof', 'LED Headlights', 'Audi Pre Sense City', 'Bang & Olufsen Sound'],
        images: [
          'https://images.unsplash.com/photo-1606664520689-16420a5a1e0b?w=800',
          'https://images.unsplash.com/photo-1606664520633-a9209e30ab43?w=800'
        ],
        description: 'The compact luxury SUV that delivers on every level. The Audi Q3 combines progressive design, innovative technology, and versatile capability in a perfectly sized package for urban adventures.',
        mpg: '23 city / 30 highway',
        engine: '2.0L TFSI 4-Cylinder Turbo',
        bodyStyle: 'SUV',
        drivetrain: 'quattro AWD',
        condition: 'New',
        stock: 'A24Q3001',
        vin: 'WAUZZZF13LN001234',
        audiTech: ['Audi Drive Select', 'MMI Touch', 'Audi Smartphone Interface']
      },
      {
        id: 2,
        make: 'Audi',
        model: 'Q5',
        year: 2024,
        price: 53900,
        type: 'SUV',
        transmission: 'S tronic',
        fuelType: 'Gasoline',
        mileage: 12,
        exterior: 'Glacier White Metallic',
        interior: 'Rock Gray Milano Leather',
        features: ['quattro AWD', 'Audi Virtual Cockpit Plus', 'Bang & Olufsen 3D Sound', 'Audi Pre Sense Front & Rear', 'Matrix LED Headlights', 'Adaptive Cruise Control', 'Wireless Charging'],
        images: [
          'https://images.unsplash.com/photo-1600706432309-817cb3872349?w=800',
          'https://images.unsplash.com/photo-1597938430467-e83351d47f54?w=800'
        ],
        description: 'The perfect balance of sophistication and versatility. The Audi Q5 delivers premium comfort, advanced quattro capability, and cutting-edge technology for every journey.',
        mpg: '22 city / 28 highway',
        engine: '2.0L TFSI 4-Cylinder Turbo',
        bodyStyle: 'SUV',
        drivetrain: 'quattro AWD',
        condition: 'New',
        stock: 'A24Q5002',
        vin: 'WAUZZZFY6P3005678',
        audiTech: ['Audi Drive Select', 'MMI Navigation Plus', 'Audi Connect Plus']
      },
      {
        id: 3,
        make: 'Audi',
        model: 'e-tron GT',
        year: 2024,
        price: 107300,
        type: 'Sports Car',
        transmission: 'Single Speed',
        fuelType: 'Electric',
        mileage: 8,
        exterior: 'Daytona Gray Pearl',
        interior: 'Arras Red Fine Nappa Leather',
        features: ['quattro Electric AWD', 'Dual Motor Setup', '469 HP', 'Air Suspension', '800V Architecture', 'Ultra-Fast Charging', 'Audi Virtual Cockpit Plus'],
        images: [
          'https://images.unsplash.com/photo-1617469767053-d3b16ee6a4ab?w=800',
          'https://images.unsplash.com/photo-1616455263449-0bd3aac04029?w=800'
        ],
        description: 'Pure emotion, electrified. The Audi e-tron GT combines breathtaking design with exhilarating electric performance, delivering up to 238 miles of range and lightning-fast charging.',
        mpg: '102 MPGe combined',
        engine: 'Dual Electric Motors',
        bodyStyle: 'Gran Turismo',
        drivetrain: 'quattro Electric AWD',
        condition: 'New',
        stock: 'A24EGT003',
        vin: 'WAUZZZGE5PB009876',
        audiTech: ['Audi Drive Select', 'MMI Touch Response', 'Audi Connect Prime']
      },
      {
        id: 4,
        make: 'Audi',
        model: 'A4',
        year: 2024,
        price: 48500,
        type: 'Sedan',
        transmission: 'S tronic',
        fuelType: 'Gasoline',
        mileage: 10,
        exterior: 'Manhattan Gray Metallic',
        interior: 'Black Milano Leather',
        features: ['quattro AWD', 'S line Package', 'Audi Virtual Cockpit', 'Audi Drive Select', 'LED Headlights', 'MMI Navigation Plus', 'Audi Pre Sense Basic'],
        images: [
          'https://images.unsplash.com/photo-1606219455063-6d703c445f80?w=800',
          'https://images.unsplash.com/photo-1606220838315-056192d5e927?w=800'
        ],
        description: 'The benchmark of progressive luxury. The Audi A4 delivers sophisticated design, exhilarating performance, and advanced technology in the perfect sport sedan package.',
        mpg: '24 city / 31 highway',
        engine: '2.0L TFSI 4-Cylinder Turbo',
        bodyStyle: 'Sedan',
        drivetrain: 'quattro AWD',
        condition: 'New',
        stock: 'A24A4004',
        vin: 'WAUZZZF25NA003421',
        audiTech: ['Audi Drive Select', 'MMI Touch', 'Audi Connect']
      },
      {
        id: 5,
        make: 'Audi',
        model: 'Q7',
        year: 2024,
        price: 72900,
        type: 'SUV',
        transmission: 'tiptronic',
        fuelType: 'Gasoline',
        mileage: 15,
        exterior: 'Samurai Gray Metallic',
        interior: 'Saiga Beige Valcona Leather',
        features: ['quattro AWD', '7-Passenger Seating', 'Bang & Olufsen 3D Premium Sound', 'Adaptive Air Suspension', 'Panoramic Sunroof', 'Four-Zone Climate Control', 'Audi Virtual Cockpit Plus'],
        images: [
          'https://images.unsplash.com/photo-1606220945770-b5b6c2c55bf1?w=800',
          'https://images.unsplash.com/photo-1606220953283-8fbd238d7959?w=800'
        ],
        description: 'The pinnacle of luxury and capability. The Audi Q7 combines spaciousness for seven with sophisticated technology, premium materials, and commanding road presence.',
        mpg: '18 city / 23 highway',
        engine: '3.0L TFSI V6 Turbo',
        bodyStyle: 'SUV',
        drivetrain: 'quattro AWD',
        condition: 'New',
        stock: 'A24Q7005',
        vin: 'WAUZZZGM3PA007654',
        audiTech: ['Audi Drive Select', 'MMI Navigation Plus', 'Audi Connect Prime']
      },
      {
        id: 6,
        make: 'Audi',
        model: 'RS 6 Avant',
        year: 2024,
        price: 119500,
        type: 'Wagon',
        transmission: 'tiptronic',
        fuelType: 'Gasoline',
        mileage: 5,
        exterior: 'Nardo Gray',
        interior: 'Black Fine Nappa Leather with Diamond Stitching',
        features: ['quattro AWD', '591 HP', 'RS Sport Suspension Plus', 'Carbon Fiber Exterior Package', 'RS Design Package', 'Bang & Olufsen Advanced Sound', 'Audi Virtual Cockpit Plus'],
        images: [
          'https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=800',
          'https://images.unsplash.com/photo-1603584173870-bed131ec1764?w=800'
        ],
        description: 'Ultimate performance meets everyday versatility. The Audi RS 6 Avant delivers supercar acceleration with wagon practicality, featuring a twin-turbo V8 and legendary quattro grip.',
        mpg: '17 city / 25 highway',
        engine: '4.0L TFSI V8 Twin-Turbo',
        bodyStyle: 'Avant',
        drivetrain: 'quattro AWD',
        condition: 'New',
        stock: 'A24RS6006',
        vin: 'WAUZZZF83NA008765',
        audiTech: ['Audi Drive Select', 'MMI Navigation Plus', 'Audi Connect Prime', 'RS Monitor']
      },
      {
        id: 7,
        make: 'Audi',
        model: 'A6',
        year: 2023,
        price: 59800,
        type: 'Sedan',
        transmission: 'S tronic',
        fuelType: 'Gasoline',
        mileage: 9500,
        exterior: 'Firmament Blue Metallic',
        interior: 'Okapi Brown Milano Leather',
        features: ['quattro AWD', 'MMI Navigation Plus', 'Audi Virtual Cockpit', 'Premium Plus Package', 'Matrix LED Headlights', 'Wireless Charging', 'Audi Pre Sense'],
        images: [
          'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=800',
          'https://images.unsplash.com/photo-1618843479302-68777f4208d3?w=800'
        ],
        description: 'Executive excellence redefined. The Audi A6 delivers sophisticated design, advanced connectivity, and refined performance in the luxury sedan segment.',
        mpg: '22 city / 29 highway',
        engine: '2.0L TFSI 4-Cylinder Turbo',
        bodyStyle: 'Sedan',
        drivetrain: 'quattro AWD',
        condition: 'Audi Certified Pre-Owned',
        stock: 'AP23A6007',
        vin: 'WAUZZZF45MA005432',
        audiTech: ['Audi Drive Select', 'MMI Touch Response', 'Audi Connect']
      },
      {
        id: 8,
        make: 'Audi',
        model: 'Q8',
        year: 2023,
        price: 76200,
        type: 'SUV',
        transmission: 'tiptronic',
        fuelType: 'Gasoline',
        mileage: 8700,
        exterior: 'Orca Black Metallic',
        interior: 'Pando Gray Valcona Leather',
        features: ['quattro AWD', 'Luxury Package', 'Bang & Olufsen 3D Premium Sound', 'Adaptive Cruise Control', 'Head-Up Display', 'Panoramic Sunroof', 'Matrix LED Headlights'],
        images: [
          'https://images.unsplash.com/photo-1606220588913-b3aacb4d2f37?w=800',
          'https://images.unsplash.com/photo-1606220588933-751c9f860ca8?w=800'
        ],
        description: 'The luxury SUV coupe that redefines presence. The Audi Q8 combines the elegance of a four-door coupe with the versatility of a large SUV and the confidence of quattro.',
        mpg: '18 city / 23 highway',
        engine: '3.0L TFSI V6 Turbo',
        bodyStyle: 'SUV Coupe',
        drivetrain: 'quattro AWD',
        condition: 'Audi Certified Pre-Owned',
        stock: 'AP23Q8008',
        vin: 'WAUZZZGN7MA001234',
        audiTech: ['Audi Drive Select', 'MMI Touch Response', 'Audi Connect Prime']
      }
    ];

    setVehicles(audiVehicles);
  }, []);

  const filteredVehicles = vehicles.filter(vehicle => {
    if (filters.model && vehicle.model !== filters.model) return false;
    if (filters.year && vehicle.year.toString() !== filters.year) return false;
    if (filters.type && vehicle.type !== filters.type) return false;
    if (filters.transmission && vehicle.transmission !== filters.transmission) return false;
    if (filters.fuelType && vehicle.fuelType !== filters.fuelType) return false;
    if (filters.bodyStyle && vehicle.bodyStyle !== filters.bodyStyle) return false;
    if (filters.drivetrain && vehicle.drivetrain !== filters.drivetrain) return false;
    if (filters.condition && vehicle.condition !== filters.condition) return false;

    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      if (max && (vehicle.price < min || vehicle.price > max)) return false;
      if (!max && vehicle.price < min) return false;
    }

    return true;
  });

  const addVehicle = (vehicle) => {
    const newVehicle = { ...vehicle, id: vehicles.length + 1 };
    setVehicles([...vehicles, newVehicle]);
  };

  const updateVehicle = (id, updates) => {
    setVehicles(vehicles.map(vehicle => 
      vehicle.id === id ? { ...vehicle, ...updates } : vehicle
    ));
  };

  const deleteVehicle = (id) => {
    setVehicles(vehicles.filter(vehicle => vehicle.id !== id));
  };

  const getVehicleById = (id) => {
    return vehicles.find(vehicle => vehicle.id === parseInt(id));
  };

  const getModelData = () => {
    const models = [
      {
        id: 'q3',
        name: 'Q3',
        category: 'SUV',
        startingPrice: 42800,
        description: 'The compact luxury SUV that delivers on every level. Progressive design meets innovative technology in a perfectly sized package for urban adventures and weekend escapes.',
        features: [
          'quattro all-wheel drive with ultra technology',
          '2.0L TFSI engine with 228 hp',
          'Audi Virtual Cockpit with 10.25" display',
          'MMI Navigation Plus with 10.1" touchscreen',
          'Panoramic sunroof',
          'Audi smartphone interface with wireless Apple CarPlay®',
          'Bang & Olufsen Premium Sound System'
        ],
        image: 'https://images.unsplash.com/photo-1606664520689-16420a5a1e0b?w=800',
        highlights: [
          { title: 'Performance', value: '228 HP' },
          { title: 'Acceleration', value: '0-60 mph in 7.0s' },
          { title: 'Cargo Space', value: '23.7 cu ft' }
        ],
        audiTechnology: [
          'Audi Drive Select',
          'MMI Touch with handwriting recognition',
          'Audi Pre Sense City'
        ]
      },
      {
        id: 'q5',
        name: 'Q5',
        category: 'SUV',
        startingPrice: 53900,
        description: 'The perfect balance of sophistication and versatility. Advanced quattro capability meets premium comfort and cutting-edge technology.',
        features: [
          'quattro all-wheel drive with ultra technology',
          '2.0L TFSI engine with 261 hp',
          'Audi Virtual Cockpit Plus with 12.3" display',
          'Bang & Olufsen 3D Premium Sound System',
          'Matrix-design LED headlights',
          'Adaptive cruise control with Traffic Jam Assist',
          'Wireless charging with signal booster'
        ],
        image: 'https://images.unsplash.com/photo-1600706432309-817cb3872349?w=800',
        highlights: [
          { title: 'Performance', value: '261 HP' },
          { title: 'Acceleration', value: '0-60 mph in 5.7s' },
          { title: 'Cargo Space', value: '26.8 cu ft' }
        ],
        audiTechnology: [
          'Audi Drive Select with Individual Mode',
          'MMI Navigation Plus',
          'Audi Connect Prime & Plus'
        ]
      },
      {
        id: 'e-tron-gt',
        name: 'e-tron GT',
        category: 'Electric',
        startingPrice: 107300,
        description: 'Pure emotion, electrified. Breathtaking design meets exhilarating electric performance with up to 238 miles of range and lightning-fast charging capability.',
        features: [
          'quattro electric all-wheel drive',
          'Dual electric motors with up to 469 hp',
          '800-volt architecture for ultra-fast charging',
          '10% to 80% charge in 22.5 minutes',
          'Adaptive air suspension with controlled damping',
          'Audi Virtual Cockpit Plus with 12.3" display',
          'Bang & Olufsen Premium Sound System'
        ],
        image: 'https://images.unsplash.com/photo-1617469767053-d3b16ee6a4ab?w=800',
        highlights: [
          { title: 'Performance', value: '469 HP' },
          { title: 'Range', value: 'Up to 238 miles' },
          { title: 'Charging', value: '10-80% in 22.5 min' }
        ],
        audiTechnology: [
          'Audi Drive Select',
          'MMI Touch Response',
          'e-tron Route Planner'
        ]
      },
      {
        id: 'a4',
        name: 'A4',
        category: 'Sedan',
        startingPrice: 48500,
        description: 'The benchmark of progressive luxury. Sophisticated design meets exhilarating performance and cutting-edge technology in the perfect sport sedan package.',
        features: [
          'quattro all-wheel drive',
          '2.0L TFSI engine with up to 261 hp',
          'Audi Virtual Cockpit with 12.3" display',
          'MMI Navigation Plus with 10.1" touchscreen',
          'Audi Pre Sense City and Basic',
          'LED headlights with DRLs',
          'SiriusXM with 3-month All Access trial'
        ],
        image: 'https://images.unsplash.com/photo-1606219455063-6d703c445f80?w=800',
        highlights: [
          { title: 'Performance', value: '261 HP' },
          { title: 'Acceleration', value: '0-60 mph in 5.6s' },
          { title: 'Efficiency', value: '31 mpg highway' }
        ],
        audiTechnology: [
          'Audi Drive Select',
          'MMI Touch',
          'Audi Connect'
        ]
      },
      {
        id: 'q7',
        name: 'Q7',
        category: 'SUV',
        startingPrice: 72900,
        description: 'The pinnacle of luxury and capability. Spaciousness for seven meets sophisticated technology, premium materials, and commanding road presence.',
        features: [
          'quattro all-wheel drive',
          '3.0L TFSI V6 engine with 335 hp',
          'Seven-passenger seating with captain\'s chairs',
          'Bang & Olufsen 3D Premium Sound System',
          'Adaptive air suspension with controlled damping',
          'Four-zone automatic climate control',
          'Panoramic sunroof'
        ],
        image: 'https://images.unsplash.com/photo-1606220945770-b5b6c2c55bf1?w=800',
        highlights: [
          { title: 'Performance', value: '335 HP' },
          { title: 'Seating', value: '7 passengers' },
          { title: 'Cargo Space', value: '69.6 cu ft max' }
        ],
        audiTechnology: [
          'Audi Drive Select',
          'MMI Navigation Plus',
          'Audi Connect Prime & Plus'
        ]
      },
      {
        id: 'rs6-avant',
        name: 'RS 6 Avant',
        category: 'Performance',
        startingPrice: 119500,
        description: 'Ultimate performance meets everyday versatility. Supercar acceleration with wagon practicality, featuring a twin-turbo V8 and legendary quattro grip.',
        features: [
          'quattro all-wheel drive with sport differential',
          '4.0L TFSI V8 twin-turbo engine with 591 hp',
          'RS sport suspension plus with Dynamic Ride Control',
          'Carbon fiber exterior styling package',
          'RS-specific interior with diamond stitching',
          'Bang & Olufsen Advanced Sound System',
          'Audi Virtual Cockpit Plus with RS-specific display'
        ],
        image: 'https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=800',
        highlights: [
          { title: 'Performance', value: '591 HP' },
          { title: 'Acceleration', value: '0-60 mph in 3.5s' },
          { title: 'Top Speed', value: '190 mph' }
        ],
        audiTechnology: [
          'Audi Drive Select with RS modes',
          'RS Monitor',
          'MMI Navigation Plus'
        ]
      },
      {
        id: 'a6',
        name: 'A6',
        category: 'Sedan',
        startingPrice: 65800,
        description: 'Executive excellence redefined. Sophisticated design meets advanced connectivity and refined performance in the luxury sedan segment.',
        features: [
          'quattro all-wheel drive',
          '2.0L or 3.0L TFSI engine options',
          'MMI Navigation Plus with MMI Touch Response',
          'Audi Virtual Cockpit Plus',
          'Bang & Olufsen Premium 3D Sound System',
          'Matrix-design LED headlights',
          'Mild hybrid technology (MHEV)'
        ],
        image: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=800',
        highlights: [
          { title: 'Performance', value: 'Up to 335 HP' },
          { title: 'Technology', value: 'Dual touchscreens' },
          { title: 'Comfort', value: 'Heated/ventilated seats' }
        ],
        audiTechnology: [
          'Audi Drive Select',
          'MMI Touch Response',
          'Audi Connect Prime & Plus'
        ]
      },
      {
        id: 'q8',
        name: 'Q8',
        category: 'SUV',
        startingPrice: 85900,
        description: 'The luxury SUV coupe that redefines presence. The elegance of a four-door coupe meets the versatility of a large SUV and the confidence of quattro.',
        features: [
          'quattro all-wheel drive',
          '3.0L TFSI V6 engine with 335 hp',
          'MMI Navigation Plus with MMI Touch Response',
          'Adaptive air suspension with controlled damping',
          'HD Matrix-design LED headlights with animation',
          'Bang & Olufsen 3D Premium Sound System',
          'Audi Virtual Cockpit Plus'
        ],
        image: 'https://images.unsplash.com/photo-1606220588913-b3aacb4d2f37?w=800',
        highlights: [
          { title: 'Performance', value: '335 HP' },
          { title: 'Styling', value: 'Coupe-inspired design' },
          { title: 'Technology', value: 'Triple digital displays' }
        ],
        audiTechnology: [
          'Audi Drive Select',
          'MMI Touch Response',
          'Audi Connect Prime & Plus'
        ]
      }
    ];

    return models;
  };

  const value = {
    vehicles,
    filteredVehicles,
    filters,
    setFilters,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    getVehicleById,
    getModelData
  };

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
};